import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { X, Heart, Gift, Share2, Send, Swords, Mic, Eye, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { wsUrl, errText, TIER_COLORS, tierPerks, tierIcon, BACKEND_URL } from "@/lib/api";
import { RubyIcon } from "@/components/Icons";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import GiftTray from "@/components/GiftTray";
import AgoraPlayer from "@/components/AgoraPlayer";
import { PKBar, PKChallengeSheet } from "@/components/PKBar";
import PKGrid, { pkCellStyle } from "@/components/PKGrid";
import { useMicSpeaker, MicButton, SpeakerChips, MicRequestsSheet } from "@/components/MicPanel";
import { GiftAnimationLayer, MarqueeBanner, EntryEffectLayer, LevelUpBanner, HeartLayer } from "@/components/GiftOverlay";

let seq = 0;

export default function StreamRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [viewers, setViewers] = useState(0);
  const [topGifters, setTopGifters] = useState([]);
  const [chatText, setChatText] = useState("");
  const [trayOpen, setTrayOpen] = useState(false);
  const [hearts, setHearts] = useState([]);
  const [animations, setAnimations] = useState([]);
  const [marquee, setMarquee] = useState(null);
  const [entry, setEntry] = useState(null);
  const [vipSeats, setVipSeats] = useState([]);
  const [levelUp, setLevelUp] = useState(null);
  const [following, setFollowing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [ended, setEnded] = useState(false);
  const [modTarget, setModTarget] = useState(null);
  const [battle, setBattle] = useState(null);
  const [pkResult, setPkResult] = useState(null);
  const [pkOpen, setPkOpen] = useState(false);
  const [micRequests, setMicRequests] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [micSheetOpen, setMicSheetOpen] = useState(false);
  const wsRef = useRef(null);
  const sendWs = useCallback((obj) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(obj));
  }, []);
  const hostAgora = location.state?.agora;
  const startMicMuted = Boolean(location.state?.mic_muted);
  const bPrefs = user?.beauty_prefs;
  const startBeauty = location.state?.beauty || bPrefs?.params || null;
  const startFilter = location.state?.filter || bPrefs?.preset || "none";
  const startSticker = location.state?.sticker || bPrefs?.sticker || "none";
  const startMakeup = location.state?.makeup || bPrefs?.makeup || "none";
  const startMirror = location.state?.mirror !== undefined ? location.state.mirror : (bPrefs?.mirror !== undefined ? bPrefs.mirror : true);

  useEffect(() => {
    const t = setInterval(() => { api.post("/rewards/watch-ping").catch(() => {}); }, 60000);
    return () => clearInterval(t);
  }, []);

  const removeAnimation = useCallback((key) => {
    setAnimations((prev) => prev.filter((x) => x.key !== key));
  }, []);

  const pushAnimation = useCallback((a) => {
    const key = ++seq;
    setAnimations((prev) => [...prev, { ...a, key }]);
    if (a.video_url) {
      setTimeout(() => setAnimations((prev) => prev.filter((x) => x.key !== key)), 120000);
      return;
    }
    const dur = a.tier === "legendary" ? 5500 : a.tier === "luxury" ? 4600 : 2700;
    setTimeout(() => setAnimations((prev) => prev.filter((x) => x.key !== key)), dur);
  }, []);

  const addGiftMessage = useCallback((sender, gift, combo) => {
    setMessages((prev) => [...prev.slice(-60), {
      key: ++seq, type: "gift", user: sender, giftIcon: gift.icon_url,
      text: `sent ${gift.icon_url ? "" : gift.emoji || ""} ${gift.name}${combo > 1 ? ` x${combo}` : ""}`,
    }]);
  }, []);

  useEffect(() => {
    api.get(`/streams/${id}`).then(({ data: d }) => {
      setData(d);
      setViewers(d.stream.viewers);
      setTopGifters(d.top_gifters || []);
      setFollowing(d.is_following);
      if (d.stream.status === "ended") setEnded(true);
    }).catch((e) => {
      toast.error(errText(e));
      navigate("/");
    });
  }, [id, navigate]);

  useEffect(() => {
    api.get(`/pk/active/${id}`).then(({ data: d }) => setBattle(d.battle)).catch(() => {});
  }, [id]);

  // Fallback: if the pk_end broadcast is missed, poll the battle once the countdown passes.
  useEffect(() => {
    if (!battle || battle.status !== "active" || !battle.ends_at) return;
    const t = setInterval(async () => {
      if (Date.now() - new Date(battle.ends_at).getTime() < 2500) return;
      try {
        const { data: d } = await api.get(`/pk/battle/${battle.id}`);
        if (d.battle.status !== "active") {
          setBattle(null);
          if (d.battle.status === "ended") {
            setPkResult(d.battle);
            setTimeout(() => setPkResult((r) => (r?.id === d.battle.id ? null : r)), 8000);
          }
        }
      } catch (e) { /* retry next tick */ }
    }, 3000);
    return () => clearInterval(t);
  }, [battle]);

  useEffect(() => {
    const ws = new WebSocket(wsUrl(id));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      switch (msg.type) {
        case "chat":
          setMessages((prev) => [...prev.slice(-60), { key: ++seq, type: "chat", user: msg.user, text: msg.text }]);
          break;
        case "viewer_count":
          setViewers(msg.count);
          break;
        case "gift": {
          setTopGifters(msg.top_gifters || []);
          if (msg.sender?.id === user?.id) break; // own gift handled optimistically
          addGiftMessage(msg.sender, msg.gift, msg.combo);
          if (msg.gift.video_url || msg.gift.tier !== "small") pushAnimation({ ...msg.gift, combo: msg.combo, senderName: msg.sender?.name });
          break;
        }
        case "entry": {
          const key = ++seq;
          setEntry({ ...msg.user, effect: msg.effect, premium: msg.premium, entry_message: msg.entry_message, key });
          setTimeout(() => setEntry((e) => (e?.key === key ? null : e)), msg.premium ? 3500 : 3300);
          if (["Kohinoor", "Maharaja", "Shahenshah"].includes(msg.user?.tier)) {
            const fanfare = new Audio("/assets/sfx/royal-fanfare.mp3");
            fanfare.volume = 0.7;
            fanfare.play().catch(() => {});
          }
          if (msg.user?.id !== user?.id)
            setMessages((prev) => [...prev.slice(-60), { key: ++seq, type: "entry", user: msg.user, text: "joined the room" }]);
          break;
        }
        case "vip_seats":
          setVipSeats(msg.users || []);
          break;
        case "marquee": {
          const key = ++seq;
          setMarquee({ text: msg.text, key });
          setTimeout(() => setMarquee((m) => (m?.key === key ? null : m)), 8200);
          break;
        }
        case "heart":
          if (msg.user?.id !== user?.id) setHearts((prev) => [...prev.slice(-15), ++seq]);
          break;
        case "level_up": {
          const key = ++seq;
          setLevelUp({ ...msg.user, key });
          setTimeout(() => setLevelUp((l) => (l?.key === key ? null : l)), 2800);
          break;
        }
        case "stream_ended":
          setEnded(true);
          break;
        case "system":
          setMessages((prev) => [...prev.slice(-60), { key: ++seq, type: "system", text: msg.text }]);
          break;
        case "error":
          toast.error(msg.text);
          break;
        case "kicked":
          if (msg.user_id === user?.id) {
            toast.error("The host removed you from this stream");
            navigate("/");
          } else {
            setMessages((prev) => [...prev.slice(-60), { key: ++seq, type: "system", text: `${msg.name} was removed by the host` }]);
          }
          break;
        case "pk_start": {
          setBattle(msg.battle);
          setPkResult(null);
          const names = (msg.battle.hosts || [msg.battle.host_a, msg.battle.host_b]).filter((h) => h && h.accepted !== false).map((h) => h.name).join(" vs ");
          setMessages((prev) => [...prev.slice(-60), { key: ++seq, type: "system", text: `⚔️ PK battle started: ${names}! Send gifts to help your host win!` }]);
          break;
        }
        case "pk_score":
          setBattle((b) => (b && b.id === msg.battle_id ? { ...b, scores: msg.scores || b.scores, score_a: msg.score_a, score_b: msg.score_b } : b));
          break;
        case "pk_end": {
          setBattle(null);
          setPkResult(msg.battle);
          setTimeout(() => setPkResult((r) => (r?.id === msg.battle.id ? null : r)), 8000);
          const wh = (msg.battle.hosts || []).find((h) => h.id === msg.battle.winner_id) || (msg.battle.winner === "a" ? msg.battle.host_a : msg.battle.winner === "b" ? msg.battle.host_b : null);
          const txt = msg.battle.winner_id === "tie" || msg.battle.winner === "tie" || !wh ? "⚔️ PK battle ended in a TIE!" : `👑 ${wh.name} won the PK battle!`;
          setMessages((prev) => [...prev.slice(-60), { key: ++seq, type: "system", text: txt }]);
          break;
        }
        case "mic_state":
          setMicRequests(msg.requests || []);
          setSpeakers(msg.speakers || []);
          break;
        default:
          break;
      }
    };
    return () => ws.close();
  }, [id, user?.id, pushAnimation, addGiftMessage, navigate]);

  const sendChat = () => {
    const text = chatText.trim();
    if (!text) return;
    if (wsRef.current?.readyState !== 1) {
      toast("Connecting to the room… try again in a second");
      return;
    }
    wsRef.current.send(JSON.stringify({ type: "chat", text }));
    setChatText("");
  };

  const tapHeart = () => {
    setHearts((prev) => [...prev.slice(-15), ++seq]);
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type: "heart" }));
  };

  useEffect(() => {
    const t = setInterval(() => setHearts((prev) => [...prev.slice(-15), ++seq]), 4500);
    return () => clearInterval(t);
  }, []);

  const sendGift = async (gift, combo) => {
    // optimistic
    const prevCoins = user.coins;
    setUser((u) => ({ ...u, coins: u.coins - gift.price * combo }));
    if (gift.video_url || gift.tier !== "small") pushAnimation({ ...gift, combo, senderName: user.name });
    addGiftMessage({ name: user.name, tier: user.tier, level: user.level }, gift, combo);
    try {
      const { data: res } = await api.post(`/streams/${id}/gift`, { gift_id: gift.id, combo });
      setUser((u) => ({ ...u, coins: res.coins, xp: res.xp, level: res.level, tier: res.tier }));
    } catch (e) {
      setUser((u) => ({ ...u, coins: prevCoins }));
      toast.error(errText(e));
    }
  };

  const toggleFollow = async () => {
    try {
      const { data: res } = await api.post(`/users/${data.stream.host.id}/follow`);
      setFollowing(res.following);
      toast.success(res.following ? `Following ${data.stream.host.name} 💜` : "Unfollowed");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Stream link copied!");
  };

  const reportStream = async () => {
    const reason = window.prompt("Why are you reporting this stream?");
    if (!reason?.trim()) return;
    try {
      await api.post("/reports", { target_type: "stream", target_id: id, reason });
      toast.success("Report submitted — our team will review it");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const moderate = async (action) => {
    if (!modTarget) return;
    try {
      await api.post(`/streams/${id}/moderate`, { user_id: modTarget.id, action });
      toast.success(`${modTarget.name} ${action}ed`);
    } catch (e) {
      toast.error(errText(e));
    }
    setModTarget(null);
  };

  const endStream = async () => {
    try {
      const { data: res } = await api.post(`/streams/${id}/end`);
      setSummary(res.summary);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const { amSpeaker } = useMicSpeaker(id, user?.id, speakers, sendWs);
  const micState = amSpeaker ? "live" : micRequests.some((r) => r.id === user?.id) ? "pending" : "idle";
  const micTap = () => {
    if (amSpeaker) {
      sendWs({ type: "mic_leave" });
    } else if (micState === "pending") {
      sendWs({ type: "mic_cancel" });
      toast("Mic request cancelled");
    } else {
      sendWs({ type: "mic_request" });
      toast("Mic request sent to the host 🎙️");
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen max-w-md mx-auto flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  const { stream, agora } = data;
  const isHost = Boolean(hostAgora || agora?.is_host);
  const tierColor = (t) => TIER_COLORS[t] || "#A0A0AB";
  const pkHosts = (battle?.hosts || []).filter((h) => h.accepted !== false);
  const multiPk = Boolean(battle && battle.status === "active" && pkHosts.length >= 2);
  const pkCellH = pkHosts.length <= 2 ? "30vh" : "21vh";
  const frameStyle = multiPk ? pkCellStyle(0, pkHosts.length) : null;

  return (
    <div className="h-screen-dvh max-w-md mx-auto relative overflow-hidden bg-black" data-testid="stream-room">
      {/* Video layer */}
      {stream.is_demo ? (
        <div className={frameStyle ? "" : "absolute inset-0"} style={frameStyle || undefined}>
          <video src={stream.video_url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${stream.video_url}` : stream.video_url} autoPlay loop muted playsInline className="w-full h-full object-cover" data-testid="demo-video" />
        </div>
      ) : (
        <AgoraPlayer agora={hostAgora || agora} isHost={isHost} startMicMuted={startMicMuted} frameStyle={frameStyle}
          initialBeauty={startBeauty} initialFilter={startFilter} initialMirror={startMirror} initialSticker={startSticker} initialMakeup={startMakeup} />
      )}
      {multiPk && (
        <>
          {pkHosts.length === 2 && (
            <div className="absolute z-[15] pointer-events-none" style={{ left: "50%", top: "84px", height: pkCellH, width: "3px", transform: "translateX(-50%) rotate(3deg)", background: "linear-gradient(180deg, transparent, #FFD86B 20%, #FFF3C4 50%, #FFD86B 80%, transparent)", boxShadow: "0 0 14px rgba(255,216,107,0.8)" }} />
          )}
          <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between pointer-events-none" style={{ ...pkCellStyle(0, pkHosts.length), background: "transparent", border: "none", top: `calc(84px + ${pkCellH} - 22px)`, height: "22px", zIndex: 16 }}>
            <span className="text-white text-[9px] font-bold truncate max-w-[60%]">{stream.host?.name}</span>
            <span className="text-[#FDE08B] text-[9px] font-extrabold" data-testid={`pk-own-score-${stream.host?.id}`}>{(battle.scores?.[stream.host?.id] || 0).toLocaleString()}</span>
          </div>
          <PKGrid battle={battle} myStreamId={id} />
        </>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70 pointer-events-none" />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-3 pt-3 flex items-center justify-between z-20">
        <div className="glass rounded-full pl-1 pr-1.5 py-1 flex items-center gap-2">
          <div className="rounded-full p-[2px] shrink-0" style={{ background: "conic-gradient(from 200deg, #FF2E97, #FFD86B, #FF2E97)" }}>
            <Avatar src={stream.host?.avatar} size={32} />
          </div>
          <div className="max-w-[104px]">
            <p className="text-white text-[13px] font-bold truncate leading-tight">{stream.host?.name}</p>
            <p className="text-[#B8B0C9] text-[10px] flex items-center gap-1 leading-tight" data-testid="viewer-count">
              <Eye size={10} /> {viewers?.toLocaleString()}
            </p>
          </div>
          {!isHost && (
            <button data-testid="follow-button" onClick={toggleFollow}
              className={`text-[10px] font-bold px-3 py-1.5 rounded-full transition-transform active:scale-95 ${following ? "bg-white/10 text-white/70" : "gradient-primary text-white shadow-[0_0_14px_rgba(255,46,151,0.4)]"}`}>
              {following ? "Following" : "Follow"}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {topGifters.length > 0 && (
            <div className="flex items-center gap-1.5 mr-0.5" data-testid="top-gifters">
              {topGifters.map((g, i) => (
                <div key={g.user_id || i} className="relative">
                  {i === 0 && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] drop-shadow-[0_0_4px_rgba(255,215,0,0.8)]">👑</span>}
                  <img src={g.avatar} alt="" className="w-7 h-7 rounded-full border-2" style={{ borderColor: ["#FFD700", "#C9CDD6", "#CD7F32"][i] }} />
                  <span className="absolute -bottom-1 -right-1 text-[8px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center text-black" style={{ background: ["#FFD700", "#C9CDD6", "#CD7F32"][i] }}>{i + 1}</span>
                </div>
              ))}
            </div>
          )}
          <button data-testid="close-stream-button" onClick={() => (isHost && !summary ? endStream() : navigate("/"))}
            className="glass-dark rounded-full w-9 h-9 flex items-center justify-center text-white">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* VIP seats (Emperor+ pinned) */}
      {vipSeats.length > 0 && (
        <div className="absolute top-14 left-3 z-20 flex items-center gap-1.5" data-testid="vip-seats">
          {vipSeats.map((v) => (
            <div key={v.id} className="relative">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px]">👑</span>
              <img src={v.avatar} alt="" className="w-7 h-7 rounded-full border-2" style={{ borderColor: v.tier_color || "#B9F2FF", boxShadow: `0 0 8px ${v.tier_color || "#B9F2FF"}` }} />
            </div>
          ))}
        </div>
      )}

      {/* Overlays */}
      <MarqueeBanner marquee={marquee} />
      <EntryEffectLayer entry={entry} />
      <LevelUpBanner levelUp={levelUp} />
      <GiftAnimationLayer animations={animations} onDone={removeAnimation} />
      <HeartLayer hearts={hearts} />
      {(battle || pkResult) && <PKBar battle={battle || pkResult} />}
      <SpeakerChips speakers={speakers} isHost={isHost}
        onMute={(uid2, muted) => sendWs({ type: "mic_mute", user_id: uid2, muted })}
        onRemove={(uid2) => sendWs({ type: "mic_remove", user_id: uid2 })} />

      {/* Right action stack */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-3" data-testid="action-stack">
        <button data-testid="action-gift-button" onClick={() => setTrayOpen(true)}
          className="glass-dark rounded-full w-11 h-11 flex items-center justify-center text-white active:scale-90 transition-transform">
          <Gift size={19} />
        </button>
        {isHost ? (
          <button data-testid="mic-requests-button" onClick={() => setMicSheetOpen(true)}
            className={`relative glass-dark rounded-full w-11 h-11 flex items-center justify-center active:scale-90 transition-transform ${micRequests.length ? "text-emerald-400" : "text-white"}`}>
            <Mic size={18} />
            {micRequests.length > 0 && (
              <span data-testid="mic-requests-badge" className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] font-extrabold flex items-center justify-center">{micRequests.length}</span>
            )}
          </button>
        ) : (
          <MicButton state={micState} onClick={micTap} />
        )}
        {isHost && !battle && (
          <button data-testid="pk-button" onClick={() => setPkOpen(true)}
            className="glass-dark rounded-full w-11 h-11 flex items-center justify-center text-pink-400 active:scale-90 transition-transform">
            <Swords size={18} />
          </button>
        )}
        <button data-testid="share-button" onClick={share}
          className="glass-dark rounded-full w-11 h-11 flex items-center justify-center text-white active:scale-90 transition-transform">
          <Share2 size={17} />
        </button>
        <button data-testid="heart-button" onClick={tapHeart}
          className="glass-dark rounded-full w-11 h-11 flex items-center justify-center text-rose-400 active:scale-90 transition-transform">
          <Heart size={19} fill="currentColor" />
        </button>
      </div>

      {/* Chat overlay */}
      <div className={`absolute ${speakers.length ? "bottom-40" : "bottom-24"} left-3 right-20 z-20 max-h-52 overflow-y-auto no-scrollbar flex flex-col justify-end gap-1.5 transition-opacity duration-500 ${entry?.premium ? "opacity-25" : "opacity-100"}`}
        style={{ maskImage: "linear-gradient(to bottom, transparent, black 18%)" }} data-testid="chat-messages">
        {messages.map((m) => (
          m.type === "system" ? (
            <div key={m.key} className="text-[10px] text-[#B8B0C9] italic bg-black/30 rounded-xl px-2.5 py-1 w-fit">⚠️ {m.text}</div>
          ) : (
          <div key={m.key} onClick={() => isHost && m.user && m.user.id !== user?.id && setModTarget(m.user)}
            className={`text-[13px] rounded-2xl px-3 py-1.5 w-fit max-w-full backdrop-blur-md ${isHost && m.user ? "cursor-pointer" : ""} ${m.type === "gift" ? "chat-gift-float bg-[#FFD86B]/15 border border-[#FFD86B]/45" : m.type === "entry" ? "bg-black/40" : m.user?.priority_chat ? "bg-black/55 border border-cyan-300/40" : `chat-bubble-${(m.user?.id?.charCodeAt(0) || 0) % 4}`}`}>
            {m.type === "gift" && <Sparkles size={12} className="inline mr-1 align-[-2px] text-[#FFD86B]" />}
            {m.user?.avatar && <img src={m.user.avatar} alt="" className="inline w-4 h-4 rounded-full mr-1 align-[-3px] border border-white/20" />}
            {m.user?.is_vip && <span className="text-[8px] bg-amber-400 text-black font-extrabold rounded px-1 mr-1 align-middle">VIP</span>}
            <span onClick={() => m.user?.id && navigate(`/profile/${m.user.id}`)} data-testid="chat-user-name"
              className={`font-bold mr-1 cursor-pointer ${m.user?.tier && tierPerks(m.user.tier).animated_badge ? "badge-shine" : ""}`}
              style={{ color: tierColor(m.user?.tier), textShadow: m.user?.name_glow ? `0 0 8px ${tierColor(m.user?.tier)}` : "none" }}>
              {m.user?.tier && tierIcon(m.user.tier) ? <img src={`${BACKEND_URL}${tierIcon(m.user.tier)}`} alt="" className="inline w-5 h-5 mr-1 align-[-4px]" style={{ filter: `drop-shadow(0 0 4px ${tierColor(m.user?.tier)}88)` }} /> : null}
              {m.user?.level ? <span className="text-[9px] bg-white/10 rounded px-1 mr-1">Lv.{m.user.level}</span> : null}
              {m.user?.name}
            </span>
            <span className={m.type === "entry" ? "text-[#B8B0C9] italic" : m.type === "gift" ? "text-[#FFD86B]" : "text-white"}>{m.text}</span>
            {m.giftIcon && <img src={`${BACKEND_URL}${m.giftIcon}`} alt="" className="inline w-5 h-5 ml-1 object-contain align-[-4px]" />}
          </div>
          )
        ))}
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 px-3 pb-5 pt-2 z-[10000] flex items-center gap-2.5">
        <div className="glass-dark rounded-full flex-1 min-w-0 flex items-center px-4 bg-black/45">
          <input
            data-testid="chat-input"
            value={chatText} onChange={(e) => setChatText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendChat()}
            placeholder="Say something…"
            className="bg-transparent outline-none flex-1 w-full min-w-0 text-white text-[13px] py-2.5 placeholder:text-[#6E6683]"
          />
          {chatText.trim()
            ? <button data-testid="chat-send-button" onClick={sendChat} className="text-[#FFD86B] p-1"><Send size={16} /></button>
            : <span className="text-[#6E6683] p-1"><Mic size={15} /></span>}
        </div>
        <button data-testid="stream-coin-pill" onClick={() => navigate("/wallet")}
          className="coin-pill px-3 py-2 shrink-0 flex items-center gap-1.5 text-[13px] active:scale-95 transition-transform">
          <img src="/assets/coin-single.png" alt="" className="w-4 h-4 object-contain" /> {user?.coins?.toLocaleString()}
        </button>
        <button data-testid="open-gift-tray-button" onClick={() => setTrayOpen(true)}
          className="gradient-primary rounded-full w-14 h-14 shrink-0 flex items-center justify-center text-white shadow-[0_0_24px_rgba(255,46,151,0.55)] active:scale-90 transition-transform">
          <Gift size={22} />
        </button>
      </div>

      <GiftTray open={trayOpen} onClose={() => setTrayOpen(false)} coins={user?.coins} userTier={user?.tier} onSend={sendGift} />
      <PKChallengeSheet open={pkOpen} onClose={() => setPkOpen(false)} myStreamId={id} />
      <MicRequestsSheet open={micSheetOpen} onClose={() => setMicSheetOpen(false)} requests={micRequests}
        onApprove={(uid2) => sendWs({ type: "mic_approve", user_id: uid2 })}
        onDeny={(uid2) => sendWs({ type: "mic_deny", user_id: uid2 })} />

      {/* Ended / summary overlay */}
      {(ended || summary) && (
        <div className="absolute inset-0 z-[10002] bg-black/90 flex flex-col items-center justify-center px-8 text-center" data-testid="stream-ended-overlay">
          <Avatar src={stream.host?.avatar} size={72} tier={stream.host?.tier} />
          <h2 className="text-white font-extrabold text-xl mt-4 font-display">{summary ? "Stream Summary" : "Stream has ended"}</h2>
          {summary && (
            <div className="grid grid-cols-2 gap-3 mt-6 w-full">
              <div className="glass-dark rounded-2xl p-3"><p className="text-2xl font-extrabold gradient-text">{Math.floor(summary.duration_seconds / 60)}m</p><p className="text-[10px] text-zinc-400">Duration</p></div>
              <div className="glass-dark rounded-2xl p-3"><p className="text-2xl font-extrabold gradient-text">{summary.peak_viewers}</p><p className="text-[10px] text-zinc-400">Peak viewers</p></div>
              <div className="glass-dark rounded-2xl p-3"><p className="text-2xl font-extrabold gradient-text flex items-center justify-center gap-1.5"><RubyIcon size={22} /> {summary.diamonds_earned?.toLocaleString()}</p><p className="text-[10px] text-zinc-400">Rubies earned</p></div>
              <div className="glass-dark rounded-2xl p-3"><p className="text-2xl font-extrabold gradient-text">{summary.total_gifts}</p><p className="text-[10px] text-zinc-400">Gifts received</p></div>
            </div>
          )}
          <button data-testid="back-home-button" onClick={() => navigate("/")}
            className="mt-8 gradient-primary text-white font-bold px-8 py-3 rounded-full active:scale-95 transition-transform">
            Back to Home
          </button>
        </div>
      )}
    </div>
  );
}
